import * as vp from 'vopay';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toast } from 'vopay';
import usePostUpdateWebhook from '../../../../hooks/useApi/usePostUpdateWebhook';
import usePostResendWebhook from '../../../../hooks/useApi/usePostResendWebhook';

import useFetchWebhookInfo from '../../../../hooks/useApi/useFetchWebhookInfo';
import UpdateWebhooksForm from './UpdateWebhooksForm';
import UpdateWebhooksReview from './UpdateWebhooksReview';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';
import UpdateWebhookContext, { UpdateWebhookCtx, WebhookUpdateError } from './Dialogue/UpdateWebhookContext';
import RetriggerWebhookContext, { RetriggerWebhookResponseCtx } from './Dialogue/RetriggerWebhookResponseContext';

import UpdateWebhookDialogue from './Dialogue/UpdateWebhookDialogue';
import {
  UpdateWebhookDialogueHandler,
  DialogueHandlerProps,
  RetriggerWebhookResponseDialogueHandler,
} from './Dialogue/types';
import { usePartnerData } from '../../../../providers/PartnerData.provider';
import useDataFetchIndicator from '../../../../hooks/useDataFetchIndicator';
import RetriggerWebhookResponseDialogue from './Dialogue/RetriggerWebhookResponseDialogue';
import RetriggerWebhookDialogue from '../Dialogue/RetriggerWebhookDialogue';
import { WebhookType } from './types';

type FC = FunctionComponent;

const UpdateWebhookContainer: FC = () => {
  const reviewRef = React.createRef<HTMLDivElement>();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks.Update' });
  const [updateErrors, setUpdateErrors] = useState<WebhookUpdateError[]>([]);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [showUpdateErrorAlert, setShowUpdateErrorAlert] = useState(false);
  const [showUpdateSuccessAlert, setShowUpdateSuccessAlert] = useState(false);
  const [, , updateWebhook] = usePostUpdateWebhook({});
  const [retriggerWebookResponse, postRetriggerWebhook] = usePostResendWebhook({});
  const [webhookDetails, getWebhookDetails] = useFetchWebhookInfo({});
  const updateWebhookRows = useBuildUpdateWebhookRows();
  const labelByEvent: Record<string, string> = Object.fromEntries(
    updateWebhookRows.map((row) => [String(row.id), String(row.label)]),
  );
  const [showRetriggerWebhookErrorAlert, setShowRetriggerWebhookErrorAlert] = useState(false);
  const [showRetriggerSuccessAlert, setShowRetriggerSuccessAlert] = useState(false);
  const [shouldRetriggerWebhook, setShouldRetriggerWebhook] = useState(false);
  const [retriggerError, setRetriggerError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [showToast, shouldShowToast] = useState(false);
  const [validatedWebhookEvents, setValidatedWebhookEvents] = useState<vp.WebhookEvent[]>([]);
  const [webhookEventType, setWebhookEventType] = useState({
    label: '',
    id: '',
  });
  const [defaultValues, setDefaultValues] = useState({
    accountonboarding: '',
  });
  const [toggleValues, setToggleValues] = useState({
    accountonboarding: false,
  });
  const [reviewing, setReviewing] = useState(false);
  const { response } = usePartnerData();
  const [webHookRequests, setWebhookRequests] = useState<vp.UpdatePartnerWebhookRequest[]>([
    {
      WebHookUrl: webhookDetails?.Webhooks ? webhookDetails?.Webhooks[0]?.Url : '',
      Disabled: false,
      Username: response.loginUsername,
      Type: 'accountonboarding',
    },
  ]);
  useEffect(() => {
    getWebhookDetails({
      Username: response.loginUsername,
    });
  }, []);
  useEffect(() => {
    if (webhookDetails?.Success) {
      const events: vp.WebhookEvent[] = [];

      Object.keys(webhookDetails?.Webhooks || {}).forEach((key) => {
        const event = webhookDetails?.Webhooks[key];
        if (event.Url !== '') {
          events.push(event.Event);
        }
      });
      if (events.length > 0) {
        setValidatedWebhookEvents(events);
      }
    }

    Object.values(webhookDetails?.Webhooks || {}).forEach((obj) => {
      switch (obj.Event) {
        case 'accountonboarding':
          setDefaultValues((prevValues) => ({
            ...prevValues,
            accountonboarding: obj.Url,
          }));
          setToggleValues((prevValues) => ({
            ...prevValues,
            accountonboarding: !!obj.Url,
          }));
          break;
        default:
          break;
      }
    });
    setWebhookRequests([
      {
        WebHookUrl: webhookDetails?.Webhooks ? webhookDetails?.Webhooks[0]?.Url : '',
        Disabled: false,
        Username: response.loginUsername,
        Type: 'accountonboarding',
      },
    ]);
  }, [webhookDetails]);

  const UpdateWebhook: UpdateWebhookCtx = {
    showUpdateSuccessAlert,
    showUpdateErrorAlert,
    updateErrors,
  };
  const RetriggerWebhookResponse: RetriggerWebhookResponseCtx = {
    showRetriggerSuccessAlert,
    retriggerError,
    showRetriggerWebhookErrorAlert,
  };
  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<UpdateWebhookDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: UpdateWebhookDialogueHandler = {
        update: {
          success: {
            default: () => {
              setShowUpdateSuccessAlert(false);
              history.push('/settings/webhooks');
            },
          },
          error: {
            default: () => {
              setShowUpdateErrorAlert(false);
              setUpdateErrors([]);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },

    handleRetriggerDialogue: (handlerProps: DialogueHandlerProps<RetriggerWebhookResponseDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: RetriggerWebhookResponseDialogueHandler = {
        error: {
          success: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
              history.push('/settings/webhooks/update');
            },
          },
          error: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
            },
          },
        },
        alert: {
          success: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
              history.push('/settings/webhooks/update');
            },
          },
          error: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },

    onReview: (values: { accountonboarding: string }) => {
      setReviewing(true);
      setDefaultValues(values);
      setWebhookRequests([
        {
          WebHookUrl: toggleValues.accountonboarding ? values.accountonboarding : '',
          Disabled: !toggleValues.accountonboarding,
          Username: response.loginUsername,
          Type: 'accountonboarding',
        },
      ]);
    },
    onUpdate: async () => {
      try {
        setDataFetchingInfo(true, t('loading', { defaultValue: 'Loading...' }), 'fullScreen');
        const results = await Promise.all(
          webHookRequests.map(async (req) => {
            try {
              const res = await updateWebhook(req);
              return { type: req.Type, success: res.Success, message: res.ErrorMessage || '' };
            } catch (error) {
              return { type: req.Type, success: false, message: error instanceof Error ? error.message : '' };
            }
          }),
        );
        const failures = results.filter((result) => !result.success);

        if (failures.length === 0) {
          setShowUpdateSuccessAlert(true);
        } else {
          setUpdateErrors(
            failures.map((failure) => ({
              label: labelByEvent[String(failure.type)] || String(failure.type),
              message: failure.message,
            })),
          );
          setShowUpdateErrorAlert(true);
        }
      } catch (error) {
        console.error('Error making webhook requests: ', error);
      } finally {
        setDataFetchingInfo(false);
      }
    },
    onBack: () => {
      setReviewing(false);
    },

    setWebhookType: (value: WebhookType) => {
      setWebhookEventType({
        label: value.label,
        id: value.id,
      });
    },
    retriggerWebhook: (value: vp.RetriggerWebhookRequest) => {
      setShouldRetriggerWebhook(true);
      shouldShowToast(true);
      postRetriggerWebhook(value);
    },
  };

  useEffect(() => {
    if (showToast) {
      shouldShowToast(false);
      setIsVisible(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (shouldRetriggerWebhook === true) {
      const { Success, ErrorMessage } = retriggerWebookResponse;

      if (Success === true) {
        setShowRetriggerSuccessAlert(true);
      } else if (Success === false) {
        setShowRetriggerWebhookErrorAlert(true);
        setRetriggerError(ErrorMessage || '');
      }

      setShouldRetriggerWebhook(false);
    }
  }, [retriggerWebookResponse]);

  useEffect(() => {
    if (!reviewing || !reviewRef.current) return;

    reviewRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [reviewing]);

  return (
    <div ref={reviewRef}>
      <UpdateWebhookContext.Provider value={UpdateWebhook}>
        <RetriggerWebhookContext.Provider value={RetriggerWebhookResponse}>
          {!reviewing && (
            <UpdateWebhooksForm
              validatedWebhookEvents={validatedWebhookEvents}
              handlers={handlers}
              defaultValues={defaultValues}
              toggleValues={toggleValues}
              setToggleValues={setToggleValues}
              onSubmit={handlers.onReview}
              setIsVisible={setIsVisible}
            />
          )}
          {reviewing && (
            <UpdateWebhooksReview
              defaultValues={defaultValues}
              toggleValues={toggleValues}
              onSubmit={handlers.onUpdate}
              onBack={handlers.onBack}
            />
          )}
          <UpdateWebhookDialogue handleDialogue={handlers.handleDialogue} />
          <RetriggerWebhookResponseDialogue handleDialogue={handlers.handleRetriggerDialogue} />
        </RetriggerWebhookContext.Provider>
      </UpdateWebhookContext.Provider>
      <RetriggerWebhookDialogue
        handlers={handlers}
        title={t('retriggerDialogTitle', { defaultValue: 'Retrigger Webhook' })}
        variant="webhook-retrigger"
        isVisible={isVisible}
        webhookEventType={webhookEventType}
        closeModal={() => setIsVisible(false)}
      />
      <Toast
        label={
          <>
            <span style={{ fontWeight: 700 }}>
              {t('retriggerToast', {
                defaultValue: '{{label}} webhook retriggered',
                label: webhookEventType.label,
              })}
            </span>
          </>
        }
        isVisible={showToast}
      />
    </div>
  );
};

export default UpdateWebhookContainer;
